export interface LoginDto {
  auth: string;
  user: UserToken;
  blob?: BlobStorageAccess;
  appInsights?: ApplicationInsights;
}

/**
 * Features of platform. Not all platforms support all integration features. Some features need API-Token which need to be setup.
 * Values of feautes:
 *   `undefined`: feature not available for the platform.
 *   `false`: it's available but not enabled due to missing permission.
 *   `true`: enabled, you can use it.
 */
export interface PlatformFeatures {
  multilang?: boolean;
  notification?: boolean;
  userpicker?: boolean;
  pluginurl?: boolean;
}

export enum PlatformPermission {
  accelerometer = 'accelerometer',
  autoplay = 'autoplay',
  camera = 'camera',
  clipboardRead = 'clipboard-read',
  clipboardWrite = 'clipboard-write',
  displayCapture = 'display-capture',
  fullscreen = 'fullscreen',
  geolocation = 'geolocation',
  gyroscope = 'gyroscope',
  magnetometer = 'magnetometer',
  microphone = 'microphone',
  pictureInPicture = 'picture-in-picture',
  screenWakeLock = 'screen-wake-lock',
  usb = 'usb',
}

export interface PlatformDetails {
  name: string; // 'staffbase', 'coyo', ...
  features: PlatformFeatures;
  permissions: PlatformPermission[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sdk: { config: { [key: string]: any }; v1: string };
}

export enum Override {
  i18n = 'i18n',
  css = 'customCSS',
  admins = 'admins',
}

export type UserRole = 'user' | 'editor' | 'admin';

export interface MapperProvidedUserToken {
  /** plugin instance id */
  instance_id: string;
  /** plugin instance name */
  instance_name: string;
  /** customer id */
  branch_id: string;
  /** customer slug */
  branch_slug: string;
  /** user id */
  sub: string;
  /** user name */
  name: string;
  /** user role */
  role: UserRole;
  /** favorite content language, f.e. 'en-US' */
  locale: string;
  /** corporate background color, f.e. '#000000' */
  theming_bg?: string;
  /** external user id - if available in the platform */
  external_id?: string;
}

export type TokenExpiration = Record<'exp' | 'nbf' | 'iat', number>;
export interface IngressTokenPayload
  extends MapperProvidedUserToken,
    TokenExpiration {
  aud: string;
  iss: string;
  permissions: PlatformPermission[];
  external_id: string;
  type: 'user';
  theming_text: string;
  features: PlatformFeatures;
}

export interface UserToken extends MapperProvidedUserToken {
  platform: PlatformDetails;
  /** plugin name */
  aud: string;
  /** middleware license id */
  license: string;
  overrides: Override[];
  type: 'user';
  external_id?: string;
}

export interface BlobStorageAccess {
  upload: {
    url: string;
    sas: string;
  };
  download: {
    url: string;
    sas: string;
  };
}

export interface ApplicationInsights {
  instrumentationKey: string;
}

/** Ensures that instance_id is provided, allowing instance-wide operations. */
export type MapperProvidedBaseApiToken = {
  /** Mapped as `external_id` in the middleware instance table.
   * The instance's primary `id` key which is also used in the target plugins
   *  is generated separately in the associated `InstanceService`'s
   * `mapAndTrackInstanceId` method. */
  instance_id: string;
  tenant_id?: string;
  sub?: string;
};

/** In addition to instance_id and optional sub property, also provide a tenant_id, allowing tenant-wide operations. */
export type MapperProvidedFullApiToken = MapperProvidedBaseApiToken & {
  tenant_id: string;
};

export interface ApiToken extends MapperProvidedFullApiToken {
  aud: string;
  license: string;
  type: 'token';
}

/** The values which are added to the x-plugin-request-origin header
 * to hint from which part of the application the request is coming from. */
export type PluginContextValues = 'admin' | 'frontoffice';
